import { useState } from 'react';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  useConfirmBookingMutation,
  useDeclineBookingRequestMutation,
} from '../store/api';

export interface PendingBookingActionsProps {
  /** Absent when the prospect never got an appointment attached (the attach
   * step is separate and can lose a race) — decline still works without it. */
  appointmentId?: string | null;
  bookingProspectId: string;
  /** Where this control is rendered — for analytics. */
  source: 'queue' | 'drawer';
  /** Called after a successful confirm or decline. */
  onResolved?: (action: 'confirmed' | 'declined') => void;
  /** Compact row layout for queue rows; default stacked banner for the drawer. */
  compact?: boolean;
}

/** One-tap confirm / decline for a pending self-booked appointment (PRD D1).
 *
 * Confirm flips only the appointment (suggested → confirmed); identity stays
 * a separate promotion step (ADR-0077). Decline discards the request and
 * cancels the pending appointment.
 */
export function PendingBookingActions({
  appointmentId,
  bookingProspectId,
  source,
  onResolved,
  compact = false,
}: PendingBookingActionsProps) {
  const [confirmBooking, { isLoading: isConfirming }] = useConfirmBookingMutation();
  const [declineBooking, { isLoading: isDeclining }] =
    useDeclineBookingRequestMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { bookingRequests, error: errorTracking } = useEventTracking();
  const [busy, setBusy] = useState(false);

  const disabled = busy || isConfirming || isDeclining;

  const handleConfirm = async () => {
    if (!appointmentId) return;
    bookingRequests.confirmClicked({ appointment_id: appointmentId, source });
    setBusy(true);
    try {
      await confirmBooking({ appointmentId }).unwrap();
      enqueueSnackbar('Booking confirmed', { variant: 'success' });
      onResolved?.('confirmed');
    } catch {
      enqueueSnackbar('Could not confirm the booking', { variant: 'error' });
      errorTracking.track('Could not confirm the booking', 'booking_confirm');
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    bookingRequests.declineClicked({
      booking_prospect_id: bookingProspectId,
      source,
    });
    setBusy(true);
    try {
      await declineBooking({ bookingProspectId }).unwrap();
      enqueueSnackbar('Booking declined', { variant: 'success' });
      onResolved?.('declined');
    } catch {
      enqueueSnackbar('Could not decline the booking', { variant: 'error' });
      errorTracking.track('Could not decline the booking', 'booking_decline');
    } finally {
      setBusy(false);
    }
  };

  const buttons = (
    <Stack direction="row" spacing={1}>
      {appointmentId && (
        <Button
          variant="contained"
          size="small"
          color="primary"
          startIcon={
            isConfirming ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <CheckCircleOutlinedIcon />
            )
          }
          onClick={handleConfirm}
          disabled={disabled}
        >
          Confirm
        </Button>
      )}
      <Button
        variant="outlined"
        size="small"
        color="inherit"
        startIcon={
          isDeclining ? <CircularProgress size={14} color="inherit" /> : <CloseIcon />
        }
        onClick={handleDecline}
        disabled={disabled}
      >
        Decline
      </Button>
    </Stack>
  );

  if (compact) {
    return buttons;
  }

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Self-booked appointment awaiting review
        </Typography>
        {buttons}
      </Box>
    </Alert>
  );
}
